/**
 * The Congregational Music Plan — what a church pays, and how it's worked out.
 *
 * Banded on average weekly attendance because that is the shape every
 * Australian church already buys music rights in: CCLI has trained them on it
 * for decades, and a familiar buying shape is worth more than a cleverer one
 * that needs explaining to a business manager at budget time.
 *
 * Worship team size is captured but deliberately does NOT affect the price. A
 * two-variable quote is materially harder to get approved than a one-variable
 * one, and the second variable buys us nothing at this size. It's there to
 * inform the product, not the invoice.
 *
 * THE NUMBERS BELOW ARE PLACEHOLDERS. Nobody has paid anything and no church
 * has been asked what they would pay. Change BANDS and PER_EXTRA_CAMPUS freely
 * — nothing else in the app reads a price except through here.
 */

/** Annual price per band, in cents. Ascending, and every band must be covered. */
export const BANDS = [
  { id: 'under_100', max: 99, label: 'Up to 100', annual: 29000 },
  { id: '100_299', max: 299, label: '100 – 299', annual: 59000 },
  { id: '300_799', max: 799, label: '300 – 799', annual: 99000 },
  { id: '800_1999', max: 1999, label: '800 – 1,999', annual: 159000 },
  { id: '2000_plus', max: Infinity, label: '2,000+', annual: 249000 },
]

/** Each campus beyond the first, annually, in cents. */
export const PER_EXTRA_CAMPUS = 24000

export const TRIAL_DAYS = 30

/**
 * GST is left OFF by default, and that is a decision to make rather than a
 * default to inherit.
 *
 * An Australian business must only charge GST if it is registered for it, and a
 * tax invoice showing GST without a real ABN behind it is a document you do not
 * want to have sent. Set both together, or neither.
 */
export const GST = {
  registered: false,
  rate: 0.1,
  // TODO(tom): the real ABN, once confirmed. Never invent one — an invoice is
  // a legal document and a wrong ABN on it is worse than an absent one.
  abn: null,
  tradingName: 'Secret Place Music',
}

export const bandFor = (attendance) =>
  attendance == null ? null : BANDS.find((b) => attendance <= b.max) ?? BANDS[BANDS.length - 1]

export const money = (cents, currency = 'AUD') =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format((cents ?? 0) / 100)

/**
 * The quote for a church, as line items plus totals.
 *
 * Returns `{ ready: false }` rather than a number when sizing is missing —
 * guessing a band from nothing would produce a confident wrong price, which on
 * a document headed to someone's accounts payable is the worst failure mode
 * available.
 */
export function quoteFor(church) {
  const band = bandFor(church?.weekly_attendance)
  if (!band) return { ready: false, reason: 'No weekly attendance on file yet.' }

  const lines = [{
    id: 'plan',
    label: 'Congregational Music Plan',
    detail: `Annual · ${band.label} weekly attendance`,
    cents: band.annual,
  }]

  const extra = Math.max((church?.campus_count ?? 1) - 1, 0)
  if (extra > 0) {
    lines.push({
      id: 'campuses',
      label: `Additional campuses (${extra})`,
      detail: 'Separate teams and administrators per site',
      cents: extra * PER_EXTRA_CAMPUS,
    })
  }

  const subtotal = lines.reduce((n, l) => n + l.cents, 0)
  const tax = GST.registered ? Math.round(subtotal * GST.rate) : 0

  return {
    ready: true,
    band,
    lines,
    subtotalCents: subtotal,
    taxCents: tax,
    totalCents: subtotal + tax,
    currency: 'AUD',
  }
}
